<template>
  <div class="feature-configs">
    <!-- Problem Solving Config -->
    <div v-if="selectedFeature === 'problemSolving'" class="config-section">
      <h4 class="config-section__title">{{ t("problem_solving_config") }}</h4>
      <div class="config-field">
        <label class="config-field__label">{{ t("solution_type") }}</label>
        <select
          v-model="localConfig.solution_type"
          class="config-field__select"
          @change="emitConfig"
        >
          <option value="">{{ t("select_solution_type") }}</option>
          <option value="direct_solution">{{ t("direct_solution") }}</option>
          <option value="step_by_step_explanation">
            {{ t("step_by_step") }}
          </option>
          <option value="in_depth_theoretical_solution">
            {{ t("theoretical_solution") }}
          </option>
          <option value="similar_equations">
            {{ t("similar_equations") }}
          </option>
          <option value="common_mistakes">{{ t("common_mistakes") }}</option>
        </select>
      </div>
    </div>

    <!-- Question Bank Config -->
    <div v-else-if="selectedFeature === 'qbank'" class="config-section">
      <h4 class="config-section__title">{{ t("question_bank_config") }}</h4>
      <div class="config-field">
        <label class="config-field__label">
          {{ t("question_count") }}:
          <strong>{{ localConfig.question_count || 15 }}</strong>
        </label>
        <input
          type="range"
          v-model.number="localConfig.question_count"
          min="5"
          max="50"
          step="5"
          class="config-field__slider"
          @input="emitConfig"
        />
        <div class="config-field__range-labels">
          <span>5</span>
          <span>50</span>
        </div>
      </div>
    </div>

    <!-- Summary Config -->
    <div v-else-if="selectedFeature === 'summary'" class="config-section">
      <h4 class="config-section__title">{{ t("summary_config") }}</h4>
      <div class="config-field">
        <label class="config-field__label">{{ t("output_type") }}</label>
        <select
          v-model="localConfig.output_type"
          class="config-field__select"
          @change="handleOutputTypeChange"
        >
          <option value="summarize">{{ t("summarize") }}</option>
          <option value="reformat">{{ t("reformat") }}</option>
          <option value="grammar_correction">
            {{ t("grammar_correction") }}
          </option>
        </select>
      </div>

      <!-- Summarize Options -->
      <div v-if="localConfig.output_type === 'summarize'" class="config-field">
        <label class="config-field__label">{{ t("summary_type") }}</label>
        <select
          v-model="localConfig.option"
          class="config-field__select"
          @change="emitConfig"
        >
          <option value="">{{ t("select_type") }}</option>
          <option value="general">{{ t("general_summary") }}</option>
          <option value="bullet_points">{{ t("bullet_points") }}</option>
          <option value="key_points">{{ t("key_points") }}</option>
          <option value="detailed">{{ t("detailed_summary") }}</option>
          <option value="executive">{{ t("executive_summary") }}</option>
        </select>
      </div>

      <!-- Reformat Options -->
      <div v-if="localConfig.output_type === 'reformat'" class="config-field">
        <label class="config-field__label">{{ t("reformat_style") }}</label>
        <select
          v-model="localConfig.option"
          class="config-field__select"
          @change="emitConfig"
        >
          <option value="">{{ t("select_style") }}</option>
          <option value="notes">{{ t("study_notes") }}</option>
          <option value="article">{{ t("article_format") }}</option>
          <option value="report">{{ t("report_format") }}</option>
          <option value="outline">{{ t("outline_format") }}</option>
        </select>
      </div>

      <!-- Grammar Correction Options -->
      <div
        v-if="localConfig.output_type === 'grammar_correction'"
        class="config-field"
      >
        <label class="config-field__checkbox">
          <input
            type="checkbox"
            v-model="localConfig.include_explanations"
            @change="emitConfig"
          />
          <span>{{ t("include_explanations") }}</span>
        </label>
      </div>
    </div>

    <!-- Flash Cards Config -->
    <div v-else-if="selectedFeature === 'flashCards'" class="config-section">
      <h4 class="config-section__title">{{ t("flash_cards_config") }}</h4>
      <div class="config-field">
        <label class="config-field__label">
          {{ t("number_of_cards") }}:
          <strong>{{ localConfig.question_count || 10 }}</strong>
        </label>
        <input
          type="range"
          v-model.number="localConfig.question_count"
          min="1"
          max="50"
          step="1"
          class="config-field__slider"
          @input="emitConfig"
        />
        <div class="config-field__range-labels">
          <span>1</span>
          <span>50</span>
        </div>
      </div>
      <div class="config-field">
        <label class="config-field__label">{{
          t("description_optional")
        }}</label>
        <textarea
          v-model="localConfig.description"
          class="config-field__textarea"
          :placeholder="t('enter_deck_description')"
          rows="2"
          @input="emitConfig"
        ></textarea>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, watch } from "vue";
import { MiaFeatureType } from "../../types";
import { useI18n } from "../../i18n";

export default {
  name: "FeatureConfigs",
  props: {
    selectedFeature: {
      type: String,
      default: null,
    },
    initialConfig: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["config-change"],
  setup(props, { emit }) {
    const { t } = useI18n();

    const localConfig = reactive({
      // Problem Solving
      solution_type: "",
      // Question Bank & Flash Cards
      question_count: 15,
      // Summary
      output_type: "summarize",
      option: "",
      include_explanations: true,
      // Flash Cards
      description: "",
    });

    // Initialize from props
    watch(
      () => props.initialConfig,
      (config) => {
        if (config) {
          Object.assign(localConfig, config);
        }
      },
      { immediate: true }
    );

    // Reset config when feature changes
    watch(
      () => props.selectedFeature,
      (feature) => {
        if (feature === MiaFeatureType.QBANK) {
          localConfig.question_count = 15;
        } else if (feature === MiaFeatureType.FLASH_CARDS) {
          localConfig.question_count = 10;
        }
        emitConfig();
      }
    );

    const emitConfig = () => {
      const config = {};

      switch (props.selectedFeature) {
        case MiaFeatureType.PROBLEM_SOLVING:
          if (localConfig.solution_type) {
            config.solution_type = localConfig.solution_type;
          }
          break;

        case MiaFeatureType.QBANK:
          config.question_count = localConfig.question_count || 15;
          break;

        case MiaFeatureType.SUMMARY:
          config.output_type = localConfig.output_type || "summarize";
          if (localConfig.option) {
            config.option = localConfig.option;
          }
          if (localConfig.output_type === "grammar_correction") {
            config.include_explanations = localConfig.include_explanations;
          }
          break;

        case MiaFeatureType.FLASH_CARDS:
          config.question_count = localConfig.question_count || 10;
          if (localConfig.description) {
            config.description = localConfig.description;
          }
          break;
      }

      emit("config-change", config);
    };

    const handleOutputTypeChange = () => {
      localConfig.option = "";
      emitConfig();
    };

    return {
      t,
      localConfig,
      emitConfig,
      handleOutputTypeChange,
    };
  },
};
</script>

<style scoped>
.feature-configs {
  padding: 0 12px;
}

.config-section {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 16px;
}

.config-section__title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 16px 0;
}

.config-field {
  margin-bottom: 16px;
}

.config-field:last-child {
  margin-bottom: 0;
}

.config-field__label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.config-field__select {
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  color: #1f2937;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.config-field__select:focus {
  outline: none;
  border-color: var(--mia-primary);
  box-shadow: 0 0 0 3px rgba(0, 196, 68, 0.1);
}

.config-field__slider {
  width: 100%;
  height: 8px;
  -webkit-appearance: none;
  appearance: none;
  background: #e5e7eb;
  border-radius: 4px;
  outline: none;
}

.config-field__slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  background: var(--mia-primary);
  border-radius: 50%;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.config-field__slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  background: var(--mia-primary);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.config-field__slider::-webkit-slider-thumb:hover {
  transform: scale(1.1);
}

.config-field__range-labels {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
}

.config-field__textarea {
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s ease;
}

.config-field__textarea:focus {
  outline: none;
  border-color: var(--mia-primary);
  box-shadow: 0 0 0 3px rgba(0, 196, 68, 0.1);
}

.config-field__checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
}

.config-field__checkbox input {
  width: 18px;
  height: 18px;
  accent-color: var(--mia-primary);
  cursor: pointer;
}
</style>
